import { Filters } from '@spo/logic';
import { VtmnBadge, VtmnChip } from '@vtmn/react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAvailableFilters } from '../../../hooks/useAvailableFilters';
import { MenuChip } from '../menuChip/MenuChip';
import { VisibleFilter } from './filters.props';

import '../navigation-bar.css';
import './filterBar.scss';

interface FiltersBarProps {
  selectedFilters: Filters;
  setSelectedFilters: (selectedFilters: Filters) => void;
  tabViewIndex: number;
}

export const FiltersBar = ({
  selectedFilters,
  setSelectedFilters,
  tabViewIndex,
}: FiltersBarProps) => {
  const { t } = useTranslation();

  const [menuFilterVisibility, setMenuFilterVisibility] =
    useState<VisibleFilter>({});

  const { allFilters, availableFilters, setAvailableFilters } =
    useAvailableFilters();

  useEffect(() => {
    allFilters && setAvailableFilters(allFilters);
  }, [allFilters]);

  return (
    <div className="navigation-bar filters-bar" data-testid="filters-bar">
      <VtmnChip
        data-testid="filters-bar_chip_product-manager"
        size="small"
        variant="single-choice"
        onClick={() =>
          setMenuFilterVisibility({
            productManager: !menuFilterVisibility.productManager,
          })
        }
        selected={
          menuFilterVisibility.productManager ||
          !!selectedFilters.productManagerIds.length
        }
      >
        {t('filters.menu.entries.product_manager')}
        {selectedFilters.productManagerIds.length > 0 && (
          <VtmnBadge value={selectedFilters.productManagerIds.length} />
        )}
      </VtmnChip>
      {menuFilterVisibility.productManager && (
        <MenuChip
          availableFilters={availableFilters.productManager}
          selectedFilters={selectedFilters}
          identifier="productManagerIds"
          setSelectedFilters={setSelectedFilters}
          setMenuFilterVisibility={setMenuFilterVisibility}
        />
      )}

      <VtmnChip
        data-testid="filters-bar_chip_product-nature"
        size="small"
        variant="single-choice"
        onClick={() =>
          setMenuFilterVisibility({
            productNature: !menuFilterVisibility.productNature,
          })
        }
        selected={
          menuFilterVisibility.productNature ||
          !!selectedFilters.productNatureIds.length
        }
      >
        {t('filters.menu.entries.product_nature')}
        {selectedFilters.productNatureIds.length > 0 && (
          <VtmnBadge value={selectedFilters.productNatureIds.length} />
        )}
      </VtmnChip>
      {menuFilterVisibility.productNature && (
        <MenuChip
          availableFilters={availableFilters.productNature}
          identifier="productNatureIds"
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          setMenuFilterVisibility={setMenuFilterVisibility}
        />
      )}

      <VtmnChip
        data-testid="filters-bar_chip_retail-family"
        size="small"
        variant="single-choice"
        onClick={() =>
          setMenuFilterVisibility({
            retailFamily: !menuFilterVisibility.retailFamily,
          })
        }
        selected={
          menuFilterVisibility.retailFamily ||
          !!selectedFilters.retailFamilyIds.length
        }
      >
        {t('filters.menu.entries.retail_family')}
        {selectedFilters.retailFamilyIds.length > 0 && (
          <VtmnBadge value={selectedFilters.retailFamilyIds.length} />
        )}
      </VtmnChip>
      {menuFilterVisibility.retailFamily && (
        <MenuChip
          availableFilters={availableFilters.retailFamily}
          identifier="retailFamilyIds"
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          setMenuFilterVisibility={setMenuFilterVisibility}
        />
      )}

      <VtmnChip
        data-testid="filters-bar_chip_user-segmentation"
        size="small"
        variant="single-choice"
        onClick={() =>
          setMenuFilterVisibility({
            userSegmentation: !menuFilterVisibility.userSegmentation,
          })
        }
        selected={
          menuFilterVisibility.userSegmentation ||
          !!selectedFilters.segmentationIds.length
        }
      >
        {t('filters.menu.entries.user_segmentation')}
        {selectedFilters.segmentationIds.length > 0 && (
          <VtmnBadge value={selectedFilters.segmentationIds.length} />
        )}
      </VtmnChip>
      {menuFilterVisibility.userSegmentation && (
        <MenuChip
          availableFilters={availableFilters.userSegmentation}
          identifier="segmentationIds"
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          setMenuFilterVisibility={setMenuFilterVisibility}
        />
      )}

      <VtmnChip
        data-testid="filters-bar_chip_sports"
        size="small"
        variant="single-choice"
        onClick={() =>
          setMenuFilterVisibility({
            sports: !menuFilterVisibility.sports,
          })
        }
        selected={
          menuFilterVisibility.sports || !!selectedFilters.sportIds.length
        }
      >
        {t('filters.menu.entries.sports')}
        {selectedFilters.sportIds.length > 0 && (
          <VtmnBadge value={selectedFilters.sportIds.length} />
        )}
      </VtmnChip>
      {menuFilterVisibility.sports && (
        <MenuChip
          availableFilters={availableFilters.sports}
          identifier="sportIds"
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          setMenuFilterVisibility={setMenuFilterVisibility}
        />
      )}

      {tabViewIndex === 1 && (
        <>
          <VtmnChip
            data-testid="filters-bar_chip_status"
            size="small"
            variant="single-choice"
            onClick={() =>
              setMenuFilterVisibility({
                status: !menuFilterVisibility.status,
              })
            }
            selected={
              menuFilterVisibility.status || !!selectedFilters.status?.length
            }
          >
            {t('filters.menu.entries.status')}
            {selectedFilters.status?.length > 0 && (
              <VtmnBadge value={selectedFilters.status.length} />
            )}
          </VtmnChip>
          {menuFilterVisibility.status && (
            <MenuChip
              availableFilters={availableFilters.status}
              identifier="status"
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              setMenuFilterVisibility={setMenuFilterVisibility}
            />
          )}
        </>
      )}
    </div>
  );
};
